import { useRef, useEffect } from 'react';
import gsap from 'gsap';

// Re-encoded from the Squarespace original (horizontal+2+(2).mp4), which was a
// 5760x2160 master at 28Mbps — 19MB for 5.5s, with its index at the end of the
// file so nothing could play until the whole tail was fetched. The banner is at
// most 1440 CSS px wide, so:
//   ffmpeg -i src.mp4 -vf scale=2400:-2 -an -c:v libx264 -preset slow -crf 24 \
//          -pix_fmt yuv420p -movflags +faststart about.mp4          (926KB)
//   ... scale=960:-2 ... -crf 27 ... about-mobile.mp4               (132KB)
// The poster is its first frame, so the banner is never an empty black box.
const VIDEO_ABOUT        = '/about/about.mp4';
const VIDEO_ABOUT_MOBILE = '/about/about-mobile.mp4';
const POSTER_ABOUT       = '/about/about-poster.webp';

// Loops silently, but only starts downloading as the section comes within a
// screen of view — the hero video gets the first seconds of bandwidth to itself.
function AboutVideo({ src, style }) {
  const ref = useRef(null);
  useEffect(() => {
    const v = ref.current;
    if (!v) return;
    const start = () => {
      if (!v.getAttribute('src')) { v.src = src; v.preload = 'auto'; }
      v.play().catch(() => {});
    };
    if (!('IntersectionObserver' in window)) { start(); return; }
    // The home page scrolls inside its own container, not the window. That
    // container clips anything below the fold, so the look-ahead margin only
    // works when it is the observer's root.
    // (outermost one below <body>: the phone layout nests its own scroll box)
    let root = null;
    for (let el = v.parentElement; el && el !== document.body; el = el.parentElement) {
      if (/(auto|scroll)/.test(getComputedStyle(el).overflowY)) root = el;
    }
    const io = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) start(); else if (v.getAttribute('src')) v.pause();
    }, { root, rootMargin: '100% 0px 100% 0px' });
    io.observe(v);
    return () => io.disconnect();
  }, [src]);
  return (
    <video
      ref={ref}
      muted loop playsInline preload="none"
      poster={POSTER_ABOUT}
      aria-hidden="true"
      style={style}
    />
  );
}

const HEADLINE = "Concert photographer, videographer, and music marketing creative — with a unique blend of visual storytelling and industry expertise.";

// ─── Desktop ──────────────────────────────────────────────────────────────────
export default function Manifesto({ width = 1440, height = 900, isMobile = false }) {
  const sectionRef    = useRef(null);
  const videoWrapRef  = useRef(null);
  const redLineRef    = useRef(null);
  const headerRef     = useRef(null);
  const wordRefs      = useRef([]);
  const detailRef     = useRef(null);
  const statsRef      = useRef(null);
  const hasRevealed   = useRef(false);

  /* ── Awwwards scroll-reveal ─────────────────────────────────────────────── */
  useEffect(() => {
    if (isMobile) return;
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const section = sectionRef.current;
    if (!section) return;

    const words = wordRefs.current.filter(Boolean);

    if (!reduced) {
      gsap.set(videoWrapRef.current, { opacity: 0, y: 36, scale: 0.96 });
      gsap.set(redLineRef.current,   { scaleX: 0, transformOrigin: 'left center' });
      gsap.set(headerRef.current,    { opacity: 0, y: 14 });
      gsap.set(words,                { opacity: 0, y: 22 });
      gsap.set(detailRef.current,    { opacity: 0, y: 18 });
      gsap.set(statsRef.current,     { opacity: 0, y: 16 });
    }

    const observer = new IntersectionObserver(([entry]) => {
      if (!entry.isIntersecting || hasRevealed.current) return;
      hasRevealed.current = true;
      observer.disconnect();
      if (reduced) return;

      const tl = gsap.timeline({ defaults: { ease: 'expo.out' } });

      // 1 — video banner breathes in
      tl.to(videoWrapRef.current, { opacity: 1, y: 0, scale: 1, duration: 1.05 });
      // 2 — red accent line sweeps left → right
      tl.to(redLineRef.current, { scaleX: 1, duration: 0.78, ease: 'power3.out' }, 0.30);
      // 3 — header fades up
      tl.to(headerRef.current, { opacity: 1, y: 0, duration: 0.52, ease: 'power2.out' }, 0.44);
      // 4 — headline words stagger in
      tl.to(words, { opacity: 1, y: 0, duration: 0.68, stagger: 0.036, ease: 'power2.out' }, 0.66);
      // 5 — detail paragraph
      tl.to(detailRef.current, { opacity: 1, y: 0, duration: 0.60, ease: 'power2.out' }, 1.10);
      // 6 — stats
      tl.to(statsRef.current, { opacity: 1, y: 0, duration: 0.55, ease: 'power2.out' }, 1.28);
    }, { threshold: 0.15 });

    observer.observe(section);
    return () => observer.disconnect();
  }, [isMobile]);

  // ── Mobile ──────────────────────────────────────────────────────────────────
  if (isMobile) {
    return (
      <div style={{ width: '100%', height: '100%', background: 'var(--ink)', overflowY: 'auto', WebkitOverflowScrolling: 'touch' }}>
        <div style={{ padding: '56px 24px 40px' }}>
          {/* Header */}
          <div className="mono" style={{ color: 'var(--bone-50)', fontSize: 10, display: 'flex', gap: 12, alignItems: 'center', marginBottom: 28 }}>
            <span style={{ color: 'var(--red)' }}>03</span>
            <span style={{ width: 28, height: 1, background: 'var(--bone-30)', display: 'inline-block' }} />
            ABOUT
          </div>

          {/* 16:9 video banner */}
          <div style={{ position: 'relative', overflow: 'hidden', marginBottom: 24, borderRadius: 2 }}>
            <div style={{ position: 'relative', paddingBottom: '56.25%', overflow: 'hidden' }}>
              <AboutVideo
                src={VIDEO_ABOUT_MOBILE}
                style={{
                  position: 'absolute', inset: 0,
                  width: '100%', height: '100%',
                  objectFit: 'cover', filter: 'none',
                }}
              />
              <div style={{
                position: 'absolute', inset: 0, pointerEvents: 'none',
                background: 'linear-gradient(to bottom, transparent 45%, rgba(0,0,0,0.70) 100%)',
              }} />
              <div className="mono" style={{
                position: 'absolute', top: 10, left: 12, zIndex: 5,
                display: 'flex', gap: 5, alignItems: 'center',
                color: 'rgba(255,42,31,0.9)', fontSize: 8, letterSpacing: '0.20em',
              }}>
                <span style={{
                  width: 4, height: 4, borderRadius: '50%',
                  background: '#ff2a1f', boxShadow: '0 0 6px rgba(255,42,31,0.9)',
                  display: 'inline-block', animation: 'rec-pulse 1.4s infinite',
                }} />
                REC
              </div>
              <div style={{ position: 'absolute', bottom: 12, left: 14, zIndex: 5 }}>
                <div className="display" style={{
                  fontSize: 16, color: 'var(--bone)', letterSpacing: '-0.02em',
                  textShadow: '0 2px 12px rgba(0,0,0,0.6)',
                }}>DREW HALLÉ</div>
              </div>
            </div>
          </div>

          <p style={{
            fontFamily: 'var(--font-serif)', fontSize: 22,
            color: 'var(--bone)', letterSpacing: '-0.01em',
            lineHeight: 1.2, margin: '0 0 16px',
          }}>
            Concert photographer, videographer, and creative — with a unique blend of storytelling and{' '}
            <em style={{ color: 'var(--red)' }}>industry expertise.</em>
          </p>

          <div style={{
            height: 1,
            background: 'linear-gradient(90deg, rgba(255,42,31,0.85) 0%, transparent 100%)',
            margin: '0 0 16px',
          }} />

          <p style={{
            fontFamily: 'var(--font-serif)', fontSize: 16,
            color: 'var(--bone-dim)', lineHeight: 1.6, margin: '0 0 14px',
          }}>
            Touring experience with multiple artists. Media credentials at major festivals across North America.
          </p>

          <p style={{
            fontFamily: 'var(--font-serif)', fontSize: 16,
            color: 'var(--bone-dim)', lineHeight: 1.6, margin: '0 0 28px',
          }}>
            Published in <span style={{ color: 'var(--bone)' }}>Billboard</span> and <span style={{ color: 'var(--bone)' }}>Variety Magazine</span>.
            Worked with <span style={{ color: 'var(--bone)' }}>Warner Music</span> and <span style={{ color: 'var(--bone)' }}>Atlantic Records</span>.
          </p>

          <div style={{
            display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14,
            paddingTop: 20, borderTop: '1px solid var(--bone-15)',
          }}>
            {[
              { k: 'PUBLISHED', v: 'BILLBOARD\nVARIETY' },
            ].map((item) => (
              <div key={item.k}>
                <div className="mono" style={{ color: 'var(--red)', fontSize: 8, marginBottom: 5 }}>{item.k}</div>
                <div className="mono" style={{ color: 'var(--bone)', fontSize: 9, lineHeight: 1.7 }}>
                  {item.v.split('\n').map((l, j) => <div key={j}>{l}</div>)}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // ── Desktop: horizontal video banner + text below ──────────────────────────
  const VIDEO_H = 424; // cinematic widescreen stripe (~47% of 900px canvas)

  return (
    <div
      ref={sectionRef}
      style={{ position: 'relative', width, height, background: 'var(--ink)', overflow: 'hidden' }}
    >

      {/* ── Full-width horizontal video banner ───────────────────────── */}
      <div
        ref={videoWrapRef}
        style={{
          position: 'absolute', top: 0, left: 0, right: 0,
          height: VIDEO_H, overflow: 'hidden',
          willChange: 'transform, opacity',
        }}
      >
        <AboutVideo
          src={VIDEO_ABOUT}
          style={{
            position: 'absolute', inset: 0,
            width: '100%', height: '100%',
            objectFit: 'cover',
            objectPosition: 'center 40%',
            background: '#050505',
          }}
        />

        {/* Film grain */}
        <div aria-hidden="true" style={{
          position: 'absolute', inset: 0, pointerEvents: 'none',
          backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='180' height='180'%3E%3Cfilter id='g'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.72' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='180' height='180' filter='url(%23g)' opacity='0.18'/%3E%3C/svg%3E")`,
          opacity: 0.38, mixBlendMode: 'overlay',
        }} />

        {/* Bottom gradient — blends video into dark bg */}
        <div style={{
          position: 'absolute', inset: 0, pointerEvents: 'none',
          background: 'linear-gradient(to bottom, rgba(0,0,0,0.08) 0%, transparent 30%, transparent 54%, rgba(3,3,3,0.94) 100%)',
        }} />

        {/* REC badge */}
        <div className="mono" style={{
          position: 'absolute', top: 62, left: 64, zIndex: 5,
          display: 'flex', gap: 7, alignItems: 'center',
          color: 'rgba(255,42,31,0.92)', fontSize: 9, letterSpacing: '0.22em',
        }}>
          <span style={{
            width: 5, height: 5, borderRadius: '50%',
            background: '#ff2a1f', boxShadow: '0 0 8px rgba(255,42,31,0.9)',
            display: 'inline-block', flexShrink: 0,
            animation: 'rec-pulse 1.4s infinite',
          }} />
          REC · 24.976
        </div>

        {/* Frame index — top right */}
        <div className="mono" style={{
          position: 'absolute', top: 62, right: 64, zIndex: 5,
          color: 'rgba(242,239,232,0.28)', fontSize: 9, letterSpacing: '0.18em',
        }}>03 / 08</div>

        {/* Name signature bottom-left of video */}
        <div style={{ position: 'absolute', bottom: 32, left: 64, zIndex: 5 }}>
          <div className="display" style={{
            fontSize: 38, color: 'var(--bone)',
            letterSpacing: '-0.03em', lineHeight: 0.88,
            textShadow: '0 4px 32px rgba(0,0,0,0.7)',
          }}>
            DREW<br />HALLÉ
          </div>
          <div className="mono" style={{
            color: 'rgba(242,239,232,0.45)', fontSize: 8.5,
            letterSpacing: '0.22em', lineHeight: 1.9, marginTop: 10,
          }}>
            PHOTOGRAPHER · VIDEOGRAPHER · CREATIVE
          </div>
        </div>
      </div>

      {/* ── Header label — floats top of section above video ─────────── */}
      <div
        ref={headerRef}
        style={{
          position: 'absolute', top: 28, left: 64, right: 64,
          zIndex: 30, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}
      >
        <div className="mono" style={{ color: 'rgba(242,239,232,0.55)', display: 'flex', gap: 16, alignItems: 'center' }}>
          <span style={{ color: 'var(--red)' }}>03</span>
          <span style={{ width: 40, height: 1, background: 'rgba(242,239,232,0.3)', display: 'inline-block' }} />
          ABOUT
        </div>
        <div className="mono" style={{ color: 'rgba(242,239,232,0.38)', fontSize: 9, letterSpacing: '0.22em' }}>
          DREW HALLÉ — PHOTOGRAPHER · VIDEOGRAPHER · CREATIVE
        </div>
      </div>

      {/* ── Red accent line — sweeps left→right after video ───────────── */}
      <div
        ref={redLineRef}
        style={{
          position: 'absolute', top: VIDEO_H, left: 0, right: 0,
          height: 2,
          background: 'linear-gradient(90deg, #ff2a1f 0%, rgba(255,42,31,0.42) 65%, transparent 100%)',
          boxShadow: '0 0 18px rgba(255,42,31,0.55), 0 0 4px rgba(255,42,31,0.80)',
          zIndex: 10,
        }}
      />

      {/* ── Bio content ───────────────────────────────────────────────── */}
      <div style={{
        position: 'absolute',
        top: VIDEO_H + 28,
        left: 64,
        right: 64,
      }}>

        {/* Headline — word-by-word stagger */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.25em', maxWidth: 860 }}>
          {HEADLINE.split(' ').map((word, i) => (
            <span
              key={i}
              ref={el => { wordRefs.current[i] = el; }}
              style={{
                display: 'inline-block',
                fontFamily: 'var(--font-serif)',
                fontSize: 32,
                fontWeight: 400,
                color: 'var(--bone)',
                letterSpacing: '-0.01em',
                lineHeight: 1.18,
                willChange: 'transform, opacity',
              }}
            >
              {word}
            </span>
          ))}
        </div>

        {/* Detail paragraph */}
        <div
          ref={detailRef}
          style={{ marginTop: 22, maxWidth: 680, willChange: 'transform, opacity' }}
        >
          <p style={{
            fontFamily: 'var(--font-serif)', fontSize: 15.5,
            color: 'var(--bone-dim)', lineHeight: 1.65, margin: 0,
          }}>
            Published in <span style={{ color: 'var(--bone)' }}>Billboard</span> and{' '}
            <span style={{ color: 'var(--bone)' }}>Variety Magazine</span>.{' '}
            Touring experience with multiple artists. Media credentials at major festivals across North America.
            Worked with <span style={{ color: 'var(--bone)' }}>Warner Music</span> and{' '}
            <span style={{ color: 'var(--bone)' }}>Atlantic Records</span>.
          </p>
        </div>

        {/* Stats grid */}
        <div
          ref={statsRef}
          style={{
            marginTop: 26, paddingTop: 20,
            borderTop: '1px solid var(--bone-15)',
            display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 32,
            maxWidth: 440,
            willChange: 'transform, opacity',
          }}
        >
          {[
            { k: 'PUBLISHED IN', v: 'BILLBOARD\nVARIETY MAGAZINE' },
          ].map((item) => (
            <div key={item.k}>
              <div className="mono" style={{ color: 'var(--red)', marginBottom: 6, fontSize: 9 }}>{item.k}</div>
              <div className="mono" style={{ color: 'var(--bone)', lineHeight: 1.75, fontSize: 10.5 }}>
                {item.v.split('\n').map((l, j) => <div key={j}>{l}</div>)}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Footer */}
      <div style={{
        position: 'absolute', bottom: 28, left: 64, right: 64,
        display: 'flex', justifyContent: 'space-between', zIndex: 20,
      }}>
        <div className="mono" style={{ color: 'var(--bone-30)' }}>[ 03 / 08 ]</div>
        <div className="mono" style={{ color: 'var(--bone-30)' }}>↓ FILM</div>
      </div>

      {/* Vignette */}
      <div style={{
        position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 0,
        background: 'radial-gradient(ellipse 120% 60% at 50% 0%, transparent 35%, rgba(0,0,0,0.38) 100%)',
      }} />
    </div>
  );
}
